import { type FC, useCallback } from "react";

import { Card, Input } from "@snapchat/snap-design-system";

import { useSelection } from "../../core/hooks/useSelection";
import { useInputState } from "../../core/validation/useInputState";
import { type NewGroup } from "../../dashboard/models";
import { cardHeaderStyle, inputSectionStyle, modalContainerStyle, modalStyle } from "../../common/styles";
import { AddLensesTable } from "../../lenses/components/AddLensesTable";
import { LensListItemModel } from "../../lenses/models";
import { GroupsRestrictions } from "../model";
import { Visible } from "../../common/components";
import { ActionModal } from "../../common/components/ActionModal";

import { labelStyle } from "./CreateGroupModal.styles";
import { CreateGroupModalText } from "./text/CreateGroupModalText";

export interface CreateGroupModalProps {
    visible: boolean;
    showLenses: boolean;
    onCancel: () => void;
    onCreate: (group: NewGroup) => void;
}

export const CreateGroupModal: FC<CreateGroupModalProps> = ({ visible, onCancel, onCreate, showLenses }) => {
    const { inputProps, isValid, reset } = useInputState<string>({
        defaultValue: "",
        error: "Name is required",
        validator: (newValue) => Boolean(newValue?.trim()),
    });

    const getKey = useCallback((item: LensListItemModel) => item.compoundId.id ?? "", []);
    const { selectedItems, toggleItemSelection, resetSelection } = useSelection<LensListItemModel>(getKey);

    const onCreateGroup = useCallback(() => {
        const lensGroupName = inputProps?.value?.trim();
        if (isValid) {
            onCreate({
                name: lensGroupName,
                lenses: [...selectedItems.values()],
            });

            onCancel();
        }
    }, [inputProps.value, isValid, onCancel, onCreate, selectedItems]);

    const resetModal = useCallback(() => {
        reset();
        resetSelection();
    }, [reset, resetSelection]);

    return (
        <ActionModal
            className={modalStyle}
            padBody={false}
            actionText={CreateGroupModalText.Action}
            visible={visible}
            onCancel={onCancel}
            onAction={onCreateGroup}
            isActionButtonDisabled={!isValid}
            didClose={resetModal}
            maskClosable={false}
            width={668}
            data-testid="create-group-modal"
        >
            <Card
                title={CreateGroupModalText.Action}
                padBody={false}
                titleBorder={false}
                className={modalContainerStyle}
                headerClassName={cardHeaderStyle}
            >
                <div className={inputSectionStyle}>
                    <Input
                        {...inputProps}
                        maxLength={GroupsRestrictions.maxGroupNameLength}
                        placeholder={CreateGroupModalText.InputPlaceholder}
                        data-testid="new-group-input"
                    />
                </div>
                <Visible when={showLenses}>
                    <div className={labelStyle}>Select any number of Lenses to add to this Lens Group</div>
                    <AddLensesTable onToggleSelection={toggleItemSelection} selectedLenses={selectedItems} />
                </Visible>
            </Card>
        </ActionModal>
    );
};
